import React, { Fragment, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FitTip } from '../../Types/MatchingProducts.ts';
import { FitTipsSelector } from '../../Selectors/MatchingProductsSelectors.ts';
import { ShowFitTipsModalSelector } from '../../Selectors/FitAssistantSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../Store/store.ts';
import { setShowFitTipsModal } from '../../Slices/FitAssistantSlice.ts';

const FitTipsAccordionContent = ({ fitTips }: { fitTips: FitTip[] }) => (
  <div className="pdpAccordion__ecomMessages">
    {fitTips.map((tip, index) => (
      <div className="pdpAccordion__ecomMessage" key={tip.FitTipCode ?? index}>
        <div className="pdpAccordion__ecomMessageHeader">
          <i className="le-icon-info" aria-hidden="true" />
          <p className="heading5 mb-2">{tip.FitTipTitle}</p>
        </div>
        <div className="pdpAccordion__messageBody paragraph text-color--gray80">{tip.FitTipDescription}</div>
      </div>
    ))}
  </div>
);

/** V2 PDP: portal fit tips into the Product Detail Info accordion. */
const FitTipsAccordion = ({ fitTips }: { fitTips: FitTip[] }) => {
  const mount = document.getElementById('REACT_fitTips_accordion_mount');

  useEffect(() => {
    const accordion = document.getElementById('pdpAccordion-productDetailInfo');
    if (!accordion || !mount) return;

    // Mount is always last in `.pdpAccordion__content`; siblings are static CMS/PIM messages.
    accordion.classList.toggle('d-none', fitTips.length === 0 && !mount.previousElementSibling);
  }, [fitTips, mount]);

  if (!mount || fitTips.length === 0) return null;
  return createPortal(<FitTipsAccordionContent fitTips={fitTips} />, mount);
};

/** V1 PDP: slide-in panel opened from the buy-area callout. */
const FitTipsSlideIn = ({ fitTips }: { fitTips: FitTip[] }) => {
  const dispatch = useAppDispatch();
  const showFitTipModal = useAppSelector(ShowFitTipsModalSelector);

  useEffect(() => {
    document.documentElement.classList.toggle('overflow-hidden', showFitTipModal);
    document.body.classList.toggle('overflow-hidden', showFitTipModal);
  }, [showFitTipModal]);

  return (
    <div className="my-3 fit-guide">
      <div
        onClick={() => dispatch(setShowFitTipsModal(!showFitTipModal))}
        className={`fit-guide-left-slide-in-menu-backdrop backdrop ${showFitTipModal ? 'active' : ''}`}
      ></div>
      <div className={`fit-guide-menu left-slide-in-menu-wrapper  ${showFitTipModal ? '' : 'wrapper-hidden'}`}>
        <section className="menu-content-section d-inline-block">
          <div className="menu-buttons slide-in-menu-heading justify-content-end">
            <button
              type="button"
              className="top-left-mini-btn hide-fit-guide-overlay d-flex fit-guide-button p-0"
              onClick={() => dispatch(setShowFitTipsModal(false))}
            >
              <i className="le-icon-x"></i>
              <span className="sr-only">Close modal</span>
            </button>
          </div>
          {fitTips.map((fitTip, index) => (
            <Fragment key={index}>
              <p className="heading4 mb-5">{fitTip.FitTipTitle}</p>
              <p className="paragraph--s mb-7 text-color--gray80">{fitTip.FitTipDescription}</p>
            </Fragment>
          ))}
        </section>
      </div>
    </div>
  );
};

const FitTipsModal = () => {
  const fitTips = useAppSelector(FitTipsSelector);
  const isProductDetailV2 = !!document.querySelector('#product-view.pdp-v2');

  return isProductDetailV2 ? <FitTipsAccordion fitTips={fitTips} /> : <FitTipsSlideIn fitTips={fitTips} />;
};

export default FitTipsModal;
